import { useEffect, useRef } from 'react';
import { useForegroundRecovery } from '@/hooks/useForegroundRecovery';

/**
 * GAPS-2 (2026-10-18) — re-read a schedule-decision panel when the Dayboard re-reads.
 *
 * ScheduleConflictPanel and RolloverDecisionPanel polled every 60 s only: after a websocket drop, a slept phone or a
 * backgrounded tab they could show a decision that was already taken (or miss a new conflict) for up to a minute.
 * This joins the two recovery signals the Dayboard already uses:
 *   - `ticket-updated` (window event) — the Dashboard dispatches it when its realtime channel rejoins after a loss,
 *     on an abnormal close and when the page becomes visible; ticket mutations dispatch it too;
 *   - useForegroundRecovery — visibilitychange / focus / online / bfcache restore.
 * Bursts of `ticket-updated` coalesce into one read (`debounceMs`). The caller keeps its own 60 s poll as the
 * backstop. Reads only; the latest `reload` is always used.
 */
export function useScheduleSignalRefresh(
  reload: () => void,
  { enabled = true, debounceMs = 300 }: { enabled?: boolean; debounceMs?: number } = {},
): void {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useForegroundRecovery(() => reloadRef.current(), { enabled });

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onTicketUpdated = () => {
      if (timer) return;
      timer = setTimeout(() => { timer = null; reloadRef.current(); }, debounceMs);
    };
    window.addEventListener('ticket-updated', onTicketUpdated);
    return () => {
      window.removeEventListener('ticket-updated', onTicketUpdated);
      if (timer) clearTimeout(timer);
    };
  }, [enabled, debounceMs]);
}
